/* ==========================================================================
   Event Collection Page – Shared Interactions
   --------------------------------------------------------------------------
   IIFE namespace module guarded against double-load (matches campaign-home
   pattern). All interactive behavior lives here so each section file can be
   kept thin. Sections expose hooks via data-* attributes and element ids.
   ========================================================================== */
(function () {
  if (window.__EventCollectionLoaded) return;
  window.__EventCollectionLoaded = true;

  var EventCollection = {
    /* -------- Hero responsive video + play/pause -------- */
    initHero: function () {
      var heroVideo = document.getElementById('evc-hero-video');
      if (!heroVideo) return;

      var hasResponsiveVideo = heroVideo.getAttribute('data-responsive-video') === 'true';
      var mq = window.matchMedia('(max-width: 760px)');

      var sync = function () {
        var wasPlaying = !heroVideo.paused && !heroVideo.ended;
        var firstLoad = !heroVideo.getAttribute('data-initialized');
        heroVideo.setAttribute('data-initialized', 'true');

        // Native sources begin loading before the shared page script is ready.
        if (!firstLoad && hasResponsiveVideo) heroVideo.load();
        if (wasPlaying || firstLoad) {
          heroVideo.play().catch(function () {});
        } else {
          heroVideo.pause();
        }
      };

      sync();
      if (hasResponsiveVideo) {
        if (typeof mq.addEventListener === 'function') {
          mq.addEventListener('change', sync);
        } else if (typeof mq.addListener === 'function') {
          mq.addListener(sync);
        }
      }

      var button = document.getElementById('evc-hero-video-button');
      var updateButton = function () {
        if (!button) return;
        var isPlaying = !heroVideo.paused && !heroVideo.ended;
        button.classList.toggle('is-playing', isPlaying);
        button.setAttribute('aria-label', isPlaying ? 'Pause hero video' : 'Play hero video');
        button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
      };

      heroVideo.addEventListener('play', updateButton);
      heroVideo.addEventListener('pause', updateButton);
      heroVideo.addEventListener('ended', updateButton);

      if (button) {
        button.addEventListener('click', function () {
          if (heroVideo.paused || heroVideo.ended) {
            heroVideo.play().catch(function () {});
          } else {
            heroVideo.pause();
          }
        });
      }
      updateButton();
    },

    /* -------- Sticky nav: first-screen offset + scroll-spy + theme toggle -------- */
    initNav: function () {
      var header = document.getElementById('evc-site-header');
      var heroSection = document.querySelector('.evc-hero-section');
      var navLinks = Array.prototype.slice.call(
        document.querySelectorAll('.evc-site-nav a[data-section]')
      );
      var navSections = Array.prototype.slice.call(
        document.querySelectorAll('.evc-page-section[id]')
      );

      if (!header || !navLinks.length) return;

      var rafId = 0;
      var activeId = '';

      /* Theme header hide/show after roughly half a viewport of scrolling */
      var themeHeaderSections = Array.prototype.slice.call(
        document.querySelectorAll('.shopify-section-group-header-group')
      );
      var themeHeaderHidden = false;

      var syncTopbarOffset = function () {
        var topbarHeight = themeHeaderSections.reduce(function (height, el) {
          if (!el || window.getComputedStyle(el).display === 'none') return height;
          return height + el.getBoundingClientRect().height;
        }, 0);

        document.documentElement.style.setProperty(
          '--evc-topbar-offset',
          Math.max(0, Math.round(topbarHeight)) + 'px'
        );
      };

      var setThemeHeaderHidden = function (hidden) {
        if (hidden === themeHeaderHidden) return;
        themeHeaderHidden = hidden;
        themeHeaderSections.forEach(function (el) {
          el.style.transition = 'transform 0.35s ease, opacity 0.3s ease';
          el.style.transform = hidden ? 'translateY(-100%)' : 'translateY(0)';
          el.style.opacity = hidden ? '0' : '1';
          el.style.pointerEvents = hidden ? 'none' : '';
        });
      };

      var setActive = function (id) {
        navLinks.forEach(function (link) {
          var isActive = link.getAttribute('data-section') === id;
          link.classList.toggle('is-active', isActive);
          if (isActive) {
            link.setAttribute('aria-current', 'page');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      };

      var syncTheme = function () {
        if (!heroSection) return;
        var rect = heroSection.getBoundingClientRect();
        var heroVisible =
          rect.top < window.innerHeight * 0.35 && rect.bottom > window.innerHeight * 0.35;
        header.classList.toggle('is-dark', heroVisible);
      };

      var updateActive = function () {
        var currentY = window.scrollY || 0;
        var heroThreshold = heroSection
          ? Math.max(220, Math.round(heroSection.offsetHeight * 0.6))
          : 220;
        if (currentY < heroThreshold) {
          if (activeId !== '') {
            activeId = '';
            setActive('');
          }
          return;
        }

        var viewportCenter = window.innerHeight * 0.46;
        var closestId = '';
        var closestDistance = Number.POSITIVE_INFINITY;

        navSections.forEach(function (section) {
          if (section === heroSection) return;
          var rect = section.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > window.innerHeight) return;
          var center = rect.top + rect.height / 2;
          var distance = Math.abs(center - viewportCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestId = section.id || '';
          }
        });

        if (closestId !== activeId) {
          activeId = closestId;
          setActive(closestId);
        }
      };

      var updateNavPosition = function () {
        var isPastHalfScreen =
          Boolean(heroSection) && (window.scrollY || 0) >= window.innerHeight * 0.5;
        header.classList.toggle('is-stuck', isPastHalfScreen);
        setThemeHeaderHidden(isPastHalfScreen);
      };

      var sync = function () {
        rafId = 0;
        syncTheme();
        updateActive();
        updateNavPosition();
      };

      syncTopbarOffset();
      sync();
      window.addEventListener(
        'scroll',
        function () {
          if (rafId) return;
          rafId = window.requestAnimationFrame(sync);
        },
        { passive: true }
      );
      window.addEventListener('resize', function () {
        if (rafId) window.cancelAnimationFrame(rafId);
        rafId = window.requestAnimationFrame(function () {
          syncTopbarOffset();
          sync();
        });
      });
    },

    /* -------- Timeline: filter dropdowns + horizontal carousel + drag -------- */
    initTimeline: function () {
      var track = document.getElementById('evc-event-timeline');
      if (!track) return;

      var scrollPrev = document.getElementById('evc-timeline-scroll-prev');
      var scrollNext = document.getElementById('evc-timeline-scroll-next');
      var resetBtn = document.getElementById('evc-timeline-reset');
      var filters = Array.prototype.slice
        .call(document.querySelectorAll('.evc-timeline-filter'))
        .map(function (filter) {
          return {
            key: filter.getAttribute('data-filter'),
            root: filter,
            trigger: filter.querySelector('.evc-timeline-filter-trigger'),
            triggerLabel: filter.querySelector('.evc-timeline-filter-trigger-label'),
            menu: filter.querySelector('.evc-timeline-filter-menu'),
            options: Array.prototype.slice.call(
              filter.querySelectorAll('.evc-timeline-filter-option')
            ),
          };
        });

      var defaults = { view: 'featured', year: 'all', type: 'all' };
      var state = { view: 'featured', year: 'all', type: 'all' };

      var updateFilterUI = function (filter, value) {
        filter.root.setAttribute('data-value', value);
        filter.options.forEach(function (option) {
          var selected = option.getAttribute('data-value') === value;
          option.classList.toggle('is-selected', selected);
          option.setAttribute('aria-checked', selected ? 'true' : 'false');
        });
        var selectedOption = filter.options.filter(function (o) {
          return o.getAttribute('data-value') === value;
        })[0];
        var label =
          (selectedOption && selectedOption.querySelector('.evc-timeline-option-text') &&
            selectedOption.querySelector('.evc-timeline-option-text').textContent.trim()) ||
          value;
        if (filter.triggerLabel) filter.triggerLabel.textContent = label;
      };

      var closeMenus = function (exceptKey) {
        filters.forEach(function (filter) {
          var keep = Boolean(exceptKey && filter.key === exceptKey);
          filter.root.classList.toggle('is-open', keep);
          filter.trigger.setAttribute('aria-expanded', keep ? 'true' : 'false');
          if (filter.menu) {
            filter.menu.hidden = !keep;
            filter.menu.setAttribute('aria-hidden', keep ? 'false' : 'true');
          }
        });
      };

      var applyFilters = function () {
        var cards = Array.prototype.slice.call(
          track.querySelectorAll('.evc-timeline-card')
        );
        var visibleIndex = 0;

        cards.forEach(function (card) {
          var year = card.getAttribute('data-year');
          var category = card.getAttribute('data-category');
          var priority = card.getAttribute('data-priority');
          var matchView = state.view === 'all' || (state.view === 'featured' && priority === 'featured');
          var matchYear = state.year === 'all' || year === state.year;
          var matchType = state.type === 'all' || category === state.type;
          var isVisible = matchView && matchYear && matchType;

          card.style.display = isVisible ? '' : 'none';
          card.classList.toggle('is-tall', isVisible && visibleIndex % 2 === 0);
          card.classList.toggle('is-short', isVisible && visibleIndex % 2 === 1);

          if (isVisible) visibleIndex += 1;
        });
        updateProgress();
      };

      var setFilter = function (key, value) {
        state[key] = value;
        var filter = filters.filter(function (f) { return f.key === key; })[0];
        if (filter) updateFilterUI(filter, value);
        applyFilters();
      };

      var updateProgress = function () {
        var maxScroll = Math.max(1, track.scrollWidth - track.clientWidth);
        if (scrollPrev) scrollPrev.disabled = track.scrollLeft <= 2;
        if (scrollNext) scrollNext.disabled = track.scrollLeft >= maxScroll - 2;
      };

      var scrollBy = function (direction) {
        var distance = Math.max(280, Math.round(track.clientWidth * 0.74));
        track.scrollBy({ left: direction * distance, behavior: 'smooth' });
      };

      if (scrollPrev) scrollPrev.addEventListener('click', function () { scrollBy(-1); });
      if (scrollNext) scrollNext.addEventListener('click', function () { scrollBy(1); });

      track.addEventListener('scroll', function () {
        window.requestAnimationFrame(updateProgress);
      });

      /* pointer drag-to-scroll with click suppression */
      var drag = { active: false, pointerId: null, startX: 0, startScroll: 0, moved: false, suppress: false };

      track.addEventListener('pointerdown', function (event) {
        if (event.button !== 0) return;
        if (event.target.closest('button, a, input, select, textarea, label')) return;
        drag.active = true;
        drag.pointerId = event.pointerId;
        drag.startX = event.clientX;
        drag.startScroll = track.scrollLeft;
        drag.moved = false;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      });

      track.addEventListener('pointermove', function (event) {
        if (!drag.active || event.pointerId !== drag.pointerId) return;
        var delta = event.clientX - drag.startX;
        if (Math.abs(delta) > 6) drag.moved = true;
        track.scrollLeft = drag.startScroll - delta;
      });

      var endDrag = function (event) {
        if (!drag.active || (event && event.pointerId !== drag.pointerId)) return;
        if (drag.moved) {
          drag.suppress = true;
          setTimeout(function () { drag.suppress = false; }, 0);
        }
        drag.active = false;
        drag.pointerId = null;
        track.classList.remove('is-dragging');
        if (event && track.hasPointerCapture(event.pointerId)) {
          track.releasePointerCapture(event.pointerId);
        }
      };

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);

      track.addEventListener(
        'click',
        function (event) {
          if (!drag.suppress) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      window.addEventListener('resize', updateProgress);

      /* dropdown open/close */
      filters.forEach(function (filter) {
        filter.trigger.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          var shouldOpen = !filter.root.classList.contains('is-open');
          closeMenus(shouldOpen ? filter.key : null);
        });
        filter.options.forEach(function (option) {
          option.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            setFilter(filter.key, option.getAttribute('data-value'));
            closeMenus();
          });
        });
      });

      if (resetBtn) {
        resetBtn.addEventListener('click', function () {
          state.view = defaults.view;
          state.year = defaults.year;
          state.type = defaults.type;
          filters.forEach(function (filter) {
            updateFilterUI(filter, state[filter.key]);
          });
          closeMenus();
          applyFilters();
        });
      }

      filters.forEach(function (filter) {
        updateFilterUI(filter, state[filter.key]);
      });

      /* ensure every menu starts closed; only a trigger click may open one */
      closeMenus();
      EventCollection.closeTimelineMenus = function () {
        closeMenus();
      };

      var closeOnOutside = function (event) {
        var insideFilter = event.target && event.target.closest
          ? event.target.closest('.evc-timeline-filter')
          : null;
        if (!insideFilter) closeMenus();
      };
      document.addEventListener('click', closeOnOutside);
      document.addEventListener('pointerdown', closeOnOutside);
      window.addEventListener('scroll', function () { closeMenus(); }, { passive: true });

      applyFilters();
    },

    /* -------- Image lightbox (timeline card expand) -------- */
    initImageLightbox: function () {
      var lightbox = document.getElementById('evc-timeline-lightbox');
      if (!lightbox) return;

      var img = lightbox.querySelector('#evc-timeline-lightbox-image');
      var title = lightbox.querySelector('#evc-timeline-lightbox-title');
      var meta = lightbox.querySelector('#evc-timeline-lightbox-meta');

      var open = function (data) {
        if (img) { img.src = data.image; img.alt = data.title || ''; }
        if (title) title.textContent = data.title || '';
        if (meta) meta.textContent = data.meta || '';
        lightbox.classList.add('is-open');
        lightbox.setAttribute('aria-hidden', 'false');
      };

      var close = function () {
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
      };

      document.addEventListener('click', function (event) {
        var trigger = event.target.closest(
          '#evc-event-timeline .evc-timeline-dot, #evc-event-timeline .evc-timeline-expand-indicator'
        );
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        open({
          image: trigger.getAttribute('data-image'),
          title: trigger.getAttribute('data-title'),
          meta: trigger.getAttribute('data-meta'),
        });
      });

      var closeBtn = document.getElementById('evc-timeline-lightbox-close');
      var closeIcon = document.getElementById('evc-timeline-lightbox-close-icon');
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (closeIcon) closeIcon.addEventListener('click', close);

      return close;
    },

    /* -------- Video lightbox (featured recap) -------- */
    initVideoLightbox: function () {
      var lightbox = document.getElementById('evc-featured-video-lightbox');
      if (!lightbox) return;

      var player = lightbox.querySelector('#evc-featured-video-player');
      var title = lightbox.querySelector('#evc-featured-video-title');
      var meta = lightbox.querySelector('#evc-featured-video-meta');
      var panel = lightbox.querySelector('.evc-featured-video-panel');

      var open = function (data) {
        if (player) player.src = data.video;
        if (title) title.textContent = data.title || '';
        if (meta) meta.textContent = data.meta || '';
        lightbox.setAttribute('data-orientation', data.orientation || 'landscape');
        if (panel) {
          panel.classList.toggle('is-portrait', data.orientation === 'portrait');
          panel.classList.toggle('is-landscape', data.orientation !== 'portrait');
        }
        lightbox.classList.add('is-open');
        lightbox.setAttribute('aria-hidden', 'false');
        if (player) { player.currentTime = 0; player.play().catch(function () {}); }
      };

      var close = function () {
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
        lightbox.setAttribute('data-orientation', '');
        if (panel) panel.classList.remove('is-portrait', 'is-landscape');
        if (player) {
          player.pause();
          player.removeAttribute('src');
          player.load();
        }
      };

      var grid = document.getElementById('evc-featured-events');
      if (grid) {
        grid.addEventListener('click', function (event) {
          var button = event.target.closest('.evc-featured-video-link');
          if (!button) return;
          open({
            video: button.getAttribute('data-video'),
            title: button.getAttribute('data-title'),
            meta: button.getAttribute('data-meta'),
            orientation: button.getAttribute('data-orientation') || 'landscape',
          });
        });
      }

      var closeBtn = document.getElementById('evc-featured-video-close');
      var closeIcon = document.getElementById('evc-featured-video-close-icon');
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (closeIcon) closeIcon.addEventListener('click', close);

      return close;
    },

    /* -------- Newsletter form (preventDefault + optional Klaviyo) -------- */
    initNewsletter: function () {
      var form = document.getElementById('evc-end-form');
      var email = document.getElementById('evc-end-email');
      var submitBtn = form && form.querySelector('button[type="submit"]');
      if (!form || !email) return;

      var companyId = (form.getAttribute('data-klaviyo-company-id') || '').trim();
      var listId = (form.getAttribute('data-klaviyo-list-id') || '').trim();

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var value = email.value.trim();
        if (!value) return;

        if (!companyId || !listId) {
          form.reset();
          return;
        }

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Submitting…';
        }

        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                list_id: listId,
                custom_source: 'Event Collection Page',
                email: value
              }
            }
          })
        })
        .then(function (res) {
          if (res.ok || res.status === 202) {
            form.reset();
            if (submitBtn) submitBtn.textContent = 'Subscribed ✓';
            setTimeout(function () {
              if (submitBtn) {
                submitBtn.textContent = form.getAttribute('data-cta-label') || 'Get Updates';
              }
            }, 3000);
          } else {
            if (submitBtn) submitBtn.textContent = 'Error, retry';
          }
        })
        .catch(function () {
          if (submitBtn) submitBtn.textContent = 'Error, retry';
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
        });
      });
    },

    /* -------- Global ESC to close menus + both lightboxes -------- */
    initEsc: function (closeImage, closeVideo) {
      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        if (EventCollection.closeTimelineMenus) EventCollection.closeTimelineMenus();
        if (closeImage) closeImage();
        if (closeVideo) closeVideo();
      });
    },

    init: function () {
      this.initHero();
      this.initNav();
      this.initTimeline();
      var closeImage = this.initImageLightbox();
      var closeVideo = this.initVideoLightbox();
      this.initNewsletter();
      this.initEsc(closeImage, closeVideo);
    },
  };

  window.EventCollection = EventCollection;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { EventCollection.init(); });
  } else {
    EventCollection.init();
  }
})();
